<template>
    <DsSheet
        v-model:open="isOpen"
        primaryAction="Save"
        secondaryAction="Cancel"
        title="Edit client"
        @clickPrimary="handleSave"
        @clickSecondary="isOpen = false"
    >
        <ClientFormFields
            v-if="clientEdit"
            v-model:client="clientEdit"
            :clientErrors="emptyErrors"
        />
    </DsSheet>
</template>

<script setup lang="ts">
import type { MultipleSelectClient, Role } from '@/company-portal/add-file/add-file.types';
import { ClientContext, getAvailableClients, updateRecipientsLists } from '@/company-portal/add-file/available-clients';
import { syncClientInfoState } from '@/company-portal/add-file/form-clients';
import DsSheet from '@/components/design-system/DsSheet.vue';
import useDsToast from '@/components/design-system/toast/useDsToast';
import type { ClientFormData, TypedClientError } from '@/components/Views/Company/File/Client/client-info-form.types';
import { ClientValidationErrorKeys } from '@/components/Views/Company/File/Client/client-info-form.types';
import ClientFormFields from '@/components/Views/Company/File/Client/ClientFormFields.vue';
import type { ClientType } from '@/components/Views/Company/File/file.types';
import { nextTick, ref, watch } from 'vue';

interface EditAvailableClientSheetProps {
    clientValue?: MultipleSelectClient['value'];
}

const props = defineProps<EditAvailableClientSheetProps>();

const isOpen = defineModel<boolean>('open', { required: true });

const { addToast } = useDsToast();

const clientEdit = ref<ClientFormData>();

const buildClientEdit = (client: MultipleSelectClient): ClientFormData => ({
    id: client.id,
    first: client.first ?? '',
    last: client.last ?? '',
    email: client.email ?? '',
    phone: client.phone ?? '',
    role: client.role as ClientType | undefined,
});

const findAvailableClient = (clientValue?: MultipleSelectClient['value']): MultipleSelectClient | undefined => {
    if (clientValue === undefined) return;
    return getAvailableClients(ClientContext.NEW).find(client => String(client.value) === clientValue);
};

watch(
    isOpen,
    isNowOpen => {
        const client = isNowOpen ? findAvailableClient(props.clientValue) : undefined;
        clientEdit.value = client ? buildClientEdit(client) : undefined;
    },
    { immediate: true },
);

const emptyErrors: TypedClientError = {
    [ClientValidationErrorKeys.EMAIL]: '',
    [ClientValidationErrorKeys.PHONE]: '',
    [ClientValidationErrorKeys.ROLE]: '',
    [ClientValidationErrorKeys.FIRST_NAME]: '',
    [ClientValidationErrorKeys.LAST_NAME]: '',
};

const handleSave = () => {
    if (!clientEdit.value) return;

    const { id, first, last, email, phone, role } = clientEdit.value;
    syncClientInfoState(id, {
        first: first ?? '',
        last: last ?? '',
        email,
        phone,
        role: role as Role | undefined, // assertion is needed because we have duplicate type declarations for client role -- @todo cleanup in [CLENG-883]
    });
    nextTick(() => updateRecipientsLists());
    addToast({ detail: 'Client updated' });

    isOpen.value = false;
};
</script>
